import { noOp } from 'common/Constants';
import { MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { getWSSPriceFromTypeByRegion } from 'common/meal-plan/ConsumerPrices';
import { isMealPlanMain } from 'common/MealPlanUtils';
import { isEqual, isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DishModalFoodItemFragment,
  MealPlanDisplayFoodItemFragment,
  MealPlanFoodItemUpsellFragment,
  MealPlanShefFragment,
  useDishModalQuery,
  useMealPlanDishCardDataQuery,
} from 'src/gqlReactTypings.generated.d';
import { DishModalRevamp } from 'src/pages/consumer/components/DishModalRevamp/DishModalRevamp';
import { CardType } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard';
import { useMealPlanDeliveryInfo } from 'src/pages/consumer/meal-plans/hooks/useMealPlanDeliveryInfo';
import { getRemainingQuantityAfterSelection } from 'src/pages/consumer/meal-plans/utils/getRemainingQuantityAfterSelection';
import { BaseDishModal, IBaseDishModalFormState } from 'src/shared/design-system/DishFirst/DishModal/BaseDishModal';
import { DEFAULT_CUSTOMIZATION_ID } from 'src/shared/design-system/DishFirst/DishModal/types';
import { useExperiment } from 'src/shared/hooks/experiments/useExperiment';
import { useIsUserInFlexServings } from 'src/shared/hooks/experiments/useIsUserInFlexServings';
import { useShouldDisplayMainsInServings } from 'src/shared/hooks/experiments/useShouldDisplayMainsInServings';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCartStore, useCartStoreSelectors } from 'src/ztore/cart-store';
import { useTasteProfileFiltersStoreSelectors } from 'src/ztore/taste-profile-filters-store';
import { useFoodItemAvailability } from 'src/ztore/wss/hooks/useFoodItemAvailability';
import { useMealPlanCatalogLookup } from 'src/ztore/wss/hooks/useMealPlanCatalog';
import { useTasteProfileForUser } from 'src/ztore/wss/hooks/useTasteProfileForUser';
import { useMealPlanCatalogStore } from 'src/ztore/wss/meal-plan-catalog-store';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { useCartActionsWithAnalytics } from '../hooks/useCartActionsWithAnalytics';
import { FoodItemChip } from './Chips/FoodItemChip';
import { DISH_IMAGE_ZOOM } from './MealPlanDishCard/consts';

interface IBaseProps {
  disabled?: boolean;
  isShowing?: boolean;
  shareUrl?: string;
  shefMenuUrl?: string;
  showAddButton?: boolean;
  showModalPrompt?: boolean;
  onClose?: () => void;
  foodItemIndex: number;
  shefIndex?: number;
  openModal?: () => void;
  upsell?: MealPlanFoodItemUpsellFragment | null;
  cardType?: CardType;
  analyticsLocation: MealPlanAnalyticsLocation;
  collectionId?: string;
}

interface IMealPlanDishModalComponentProps extends IBaseProps {
  foodItem: MealPlanDisplayFoodItemFragment;
  loadedData?: DishModalFoodItemFragment;
  shef: MealPlanShefFragment;
}

const MealPlanDishModalComponent: React.FC<IMealPlanDishModalComponentProps> = (props) => {
  const {
    foodItem,
    shef,
    upsell,
    onClose: handleClose = noOp,
    foodItemIndex,
    shefIndex,
    cardType,
    analyticsLocation,
    showAddButton,
    collectionId,
  } = props;

  const { getNumRemainingMains, numServings: storeNumServings } = useCartStoreSelectors();
  const { getMealPlanForUser } = useMealPlanForUserStoreSelectors();
  const { zipCode } = getMealPlanForUser() ?? {};
  const regionId = zipCode?.regionId;
  const numServings = storeNumServings ?? 1;
  const isFlexServings = useIsUserInFlexServings();
  const displayQuantityInServings = useShouldDisplayMainsInServings();
  const { numAvailableUnits, numUnitsInCart, numAvailableServings, numServingsInCart } = useFoodItemAvailability(
    foodItem,
    shef
  );
  const foodItemIsMain = isMealPlanMain(foodItem);
  const flexMaxServings = numServingsInCart + numAvailableServings;
  const servingsPerMeal = foodItemIsMain && !isFlexServings ? numServings : 1;
  const maxUnits = Math.max(
    numUnitsInCart,
    foodItemIsMain
      ? Math.min(getNumRemainingMains() + numUnitsInCart, numAvailableUnits + numUnitsInCart)
      : numAvailableUnits + numUnitsInCart
  );
  const maxQuantity = isFlexServings && foodItemIsMain ? flexMaxServings : maxUnits;

  const {
    isTreatment: isDishModalRevampTreatment,
    isEligible: isDishModalEligible,
    logExposure: logDishModalExposure,
  } = useExperiment(ExperimentNames.MEAL_PLAN_DISH_MODAL_REFRESH);

  // Modal events inherit the favorites surface so analytics keep favorites separate from other dish-modal opens.
  const fromFavorites = analyticsLocation === 'favorites-tab' || analyticsLocation === 'favorites-shef-menu';
  const dishModalAnalyticsLocation: MealPlanAnalyticsLocation = fromFavorites ? 'favorites-dish-modal' : 'dish-modal';

  const { setQuantityInCart, setServingsInCart, flexFirstAddServings } = useCartActionsWithAnalytics({
    foodItem,
    shef,
    analyticsLocation: dishModalAnalyticsLocation,
    foodItemPosition: foodItemIndex,
    shefPosition: shefIndex,
    collectionId,
  });

  const minQuantityForModal = (isFlexServings ? numServingsInCart : numUnitsInCart) > 0 ? 0 : 1;

  const inCartModalQuantity = isFlexServings ? numServingsInCart : numUnitsInCart;
  const isFlexMain = isFlexServings && foodItemIsMain;
  const emptyModalQuantity = isFlexMain ? Math.max(1, Math.min(flexFirstAddServings, flexMaxServings)) : 1;
  const defaultModalQuantity = inCartModalQuantity > 0 ? inCartModalQuantity : emptyModalQuantity;

  const [formState, setFormState] = useState<IBaseDishModalFormState>({
    spiceLevelCustomizationId: DEFAULT_CUSTOMIZATION_ID,
    quantity: defaultModalQuantity,
  });

  const bottomLeftChip = (
    <FoodItemChip
      remainingAvailableUnits={getRemainingQuantityAfterSelection({
        availableQuantityExcludingCart: displayQuantityInServings ? numAvailableServings : numAvailableUnits,
        quantityInCart: displayQuantityInServings ? numServingsInCart : numUnitsInCart,
        uncommittedQuantity: formState.quantity,
      })}
      unitsInCart={formState.quantity}
    />
  );

  const formInitialState: IBaseDishModalFormState = useMemo(
    () => ({
      spiceLevelCustomizationId: DEFAULT_CUSTOMIZATION_ID,
      quantity: defaultModalQuantity,
    }),
    [defaultModalQuantity]
  );

  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!props.isShowing) {
      isSubmittingRef.current = false;
      return;
    }
    if (isSubmittingRef.current) return;
    setFormState((prev) => ({ ...prev, quantity: defaultModalQuantity }));
  }, [props.isShowing, defaultModalQuantity]);

  const submit = useCallback(() => {
    if (!showAddButton || !shef) return;
    isSubmittingRef.current = true;
    if (isFlexServings) setServingsInCart(formState.quantity);
    else setQuantityInCart(formState.quantity);
    setTimeout(handleClose, 250);
  }, [isFlexServings, setServingsInCart, setQuantityInCart, formState.quantity, handleClose, shef, showAddButton]);

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      setFormState({ ...formState, quantity: newQuantity });
    },
    [setFormState, formState]
  );

  const upsellPrice = useMemo(
    () =>
      upsell
        ? (upsell?.price ?? 0) * formState.quantity * (cardType === CardType.Small ? 1 : servingsPerMeal ?? 1)
        : null,
    [formState.quantity, servingsPerMeal, upsell?.price, cardType]
  );

  const updatedPrice =
    cardType === CardType.Small || cardType === CardType.SmallWithShefName
      ? (upsellPrice ?? 0) +
        getWSSPriceFromTypeByRegion(foodItem.mealPlanFoodType ?? '', regionId ?? 0) * formState.quantity
      : upsellPrice;

  const priceToDisplay = updatedPrice;

  useEffect(() => {
    if (!props.isShowing) return;

    if (isDishModalEligible) {
      logDishModalExposure();
    }
  }, [props.isShowing, isDishModalEligible]);

  const renderDishModalRevamp = (
    <DishModalRevamp
      isShowing={props.isShowing}
      foodItem={foodItem}
      shef={shef}
      unitsInCart={numUnitsInCart}
      handleClose={handleClose}
      servingsPerMeal={servingsPerMeal}
      showAddButton={showAddButton}
      productType='subscriptions'
      location={analyticsLocation}
      analyticsLocation={dishModalAnalyticsLocation}
      collectionId={collectionId}
      quantity={formState.quantity}
      onQuantityChange={handleQuantityChange}
      minQuantity={minQuantityForModal}
      maxQuantity={maxQuantity}
      isServingSizeSelected={true}
      isEditingCart={numUnitsInCart > 0}
      hasBeenUpdated={!isEqual(formState, formInitialState)}
      actionButtonClick={submit}
      priceToDisplay={priceToDisplay}
      bottomLeftChip={bottomLeftChip}
    />
  );

  return isDishModalRevampTreatment && !isFlexServings ? (
    renderDishModalRevamp
  ) : (
    <BaseDishModal
      {...props}
      foodItem={foodItem}
      imageZoom={DISH_IMAGE_ZOOM}
      maxQuantity={maxQuantity}
      canSubmitCart={!isEqual(formState, formInitialState)}
      submit={submit}
      minQuantity={minQuantityForModal}
      productType='subscriptions'
      isEditingCart={(isFlexServings ? numServingsInCart : numUnitsInCart) > 0}
      unitsInCart={numUnitsInCart}
      formState={formState}
      upsell={upsell}
      showPortionSizeControls={false}
      setFormState={setFormState}
      cardType={cardType}
      servingsPerMeal={servingsPerMeal}
      location={analyticsLocation}
      analyticsLocation={dishModalAnalyticsLocation}
      collectionId={collectionId}
      regionId={regionId}
      bottomLeftChip={bottomLeftChip}
      quantityUnit={displayQuantityInServings ? 'serving' : undefined}
      addButtonText={displayQuantityInServings ? 'Add to order' : undefined}
    />
  );
};

interface IMealPlanDishModal extends IBaseProps {
  shefId: string;
  foodItemId: number;
}

const WrapperMealPlanDishModal: React.FC<IMealPlanDishModal> = ({ foodItemId, shefId, ...props }) => {
  const location = useLocation();
  const { tasteProfile } = useTasteProfileForUser();
  const hasKids = (tasteProfile?.numLittleEaters ?? 0) + (tasteProfile?.numBigEaters ?? 0) > 0;
  const isOnCheckout = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_CHECKOUT]);
  const deliveryDate = useCartStore((state) => state.deliveryDate);
  const { regionId } = useMealPlanDeliveryInfo();
  const { data: dishModalData, loading: dishModalLoading } = useDishModalQuery({
    variables: { foodItemId },
    skip: isNil(shefId),
  });

  const { foodItem: cachedFoodItem, shef: cachedShef } = useMealPlanCatalogLookup(foodItemId, shefId);
  const setFoodItem = useMealPlanCatalogStore((s) => s.setFoodItem);
  const setShef = useMealPlanCatalogStore((s) => s.setShef);

  const filters = useTasteProfileFiltersStoreSelectors.use.filters();
  const replacementDietaryRestrictions = filters?.replacementDietaryRestrictions;
  const { data, loading: cardDataLoading } = useMealPlanDishCardDataQuery({
    variables: {
      foodItemId,
      shefId,
      deliveryDate,
      replacementDietaryRestrictions,
      hasKids,
      isOnCheckout,
      regionId,
    },
    fetchPolicy: 'cache-first',
    skip: !!(cachedFoodItem && cachedShef),
    onCompleted: (result) => {
      if (result.foodItem) setFoodItem(result.foodItem);
      if (result.shef) setShef(result.shef);
    },
  });

  const foodItem = cachedFoodItem ?? data?.foodItem;
  const shef = cachedShef ?? data?.shef;

  if (dishModalLoading || cardDataLoading || !foodItem || !shef) {
    return null;
  }

  return <MealPlanDishModalComponent {...props} foodItem={foodItem} shef={shef} loadedData={dishModalData?.foodItem} />;
};

export default WrapperMealPlanDishModal;
